import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Sheet, SheetContent } from '@voll-intelligence/ui-kit/ui/sheet';
import { usePushDrawerMarker } from './push_drawer_marker.js';
import './flight_detail_drawer.css';

/**
 * Shell do slide-over "Detalhe do voo" — o `Sheet` do DS à direita (`medium`, 576)
 * com cabeçalho (botão voltar + título), corpo rolável e rodapé opcional. PURO/display-only:
 * recebe strings já localizadas pelo chamador (sem i18n próprio) e o conteúdo do
 * corpo/rodapé como `children`/`footer`.
 *
 * Fonte única do shell dos dois pontos de detalhe do voo: o drawer do web-chat
 * (ofertas, pernas reais + push da conversa) e o `SegmentDetailDrawer` dos cards
 * in-chat (perna única). O véu vem do `Sheet` quando `modal`; o PUSH é do shell —
 * enquanto aberto ele marca `:root[data-push-drawer-open]` (via
 * `usePushDrawerMarker`), e o host empurra a conversa via CSS (web-chat) ou ignora
 * e segue em overlay (console do operador).
 * Esc e o botão voltar disparam `onClose`; o foco inicial vai para o botão voltar
 * e, ao desmontar, volta para quem abriu o painel.
 *
 * `modal` (default `true`) é o `modal` do `Sheet`: com véu (cards e abaixo de
 * `column`) a página fica inerte e o Tab fica no painel; no push das ofertas a
 * conversa segue interativa ao lado ⇒ `modal={false}`: sem véu, sem trap, e o
 * clique na conversa não fecha o painel.
 */
export function FlightDetailDrawerShell({
  title,
  ariaLabel,
  backLabel,
  modal = true,
  onClose,
  footer,
  children,
}: {
  readonly title: string;
  readonly ariaLabel: string;
  readonly backLabel: string;
  readonly modal?: boolean;
  readonly onClose: () => void;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}) {
  const backRef = useRef<HTMLButtonElement>(null);

  // Contrato de "push": marca a raiz enquanto aberto (web-chat empurra `.chat-main`
  // e o explorer; console do operador ignora e segue em overlay). Compartilhado
  // com o filtro de voos via `usePushDrawerMarker`.
  usePushDrawerMarker();

  // Devolve o foco a quem ABRIU quando o painel desmonta (voltar, Esc, véu):
  // sem isto o foco caía no body — e, no regime modal (overlay abaixo de
  // `column`), a TA perdia o lugar na lista. Só o elemento que estava focado na
  // MONTAGEM, e só se ainda estiver no DOM (uma lista que remontou não ganha
  // foco fantasma). Deps vazias de propósito: trocar o corpo (mapa de assentos
  // ↔ detalhe) não desmonta o shell, e um `onClose` com identidade nova não
  // pode re-disparar a captura/restauração.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <Sheet
      open
      modal={modal}
      // No push a conversa ao lado segue clicável: clicar nela não fecha o painel.
      disablePointerDismissal={!modal}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        size="medium"
        showCloseButton={false}
        aria-label={ariaLabel}
        initialFocus={backRef}
        className="fdv-panel gap-0"
      >
        <header className="fdv-header">
          <button
            ref={backRef}
            type="button"
            className="fdv-back"
            onClick={onClose}
            aria-label={backLabel}
          >
            <ArrowLeftIcon size={20} aria-hidden />
          </button>
          <h2 className="fdv-title">{title}</h2>
        </header>

        <div className="fdv-scroll scrollbar-rail [scrollbar-gutter:auto]!">{children}</div>

        {footer && <footer className="fdv-foot">{footer}</footer>}
      </SheetContent>
    </Sheet>
  );
}
